import { Bell, Search } from "lucide-react";
import React, { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { navbarItems } from "../core/config";

import { App_Url_New } from "@/src_new/shared/common/static";
import CommonLogoutModal from "@/src_new/shared/components/CommonLogoutModal";
import { getThemeClass, getThemeComponentGroup } from "@/src_new/shared/lib/themeComponentClasses";
import { openModal } from "@/src_new/shared/redux/reducers/uiSlice";
import { usePosterReducers } from "@/src_new/shared/redux/store/useGetData";

export default function DashboardHeader(): React.JSX.Element {
  const pageShellClasses = getThemeComponentGroup("pageShell");
  const cardClasses = getThemeComponentGroup("card");
  const {
    auth: { user },
  } = usePosterReducers();
  const path = useLocation().pathname;
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const getPageTitle = (path, navbarItems) => {
    const cleanPath = path.split("?")[0];
    const search = useLocation().search;

    const matchedItem = navbarItems.find((item) => item.path === cleanPath);
    const queryParams = new URLSearchParams(search);
    const nameParam = queryParams.get("name");

    if (nameParam) {
      const result = nameParam.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
      return result;
    }

    const segments = cleanPath.split("/").filter(Boolean);
    const lastSegment = segments[segments.length - 1] || "dashboard";
    // capitalize first letter
    return lastSegment.charAt(0).toUpperCase() + lastSegment.slice(1);
  };

  const title = getPageTitle(path, navbarItems);
  const getInitials = (fullName) => {
    if (!fullName) return "U";

    const names = fullName.trim().split(" ").filter(Boolean);

    if (names.length === 1) {
      return names[0][0].toUpperCase();
    }

    return (names[0][0] + names[names.length - 1][0]).toUpperCase();
  };

  const userName = getInitials(user?.user?.full_name);
  console.log("title", userName);

  // function callLogout() {
  //   dispatch(logout());

  //   setTimeout(() => {navigate(App_Url_New.secured.frontlinks.login);
  //   }, 0);
  // }

  const handleSignOut = () => {
    dispatch(
      openModal({
        name: App_Url_New.PopupKeys.LOGOUT_CONFIRM,
        title: "Logout?",
        className: "max-w-md",
        children: <CommonLogoutModal displayName={userName} />,
      })
    );
  };

  return (
    <header
      className={`h-16 border-b flex items-center justify-between px-4 md:px-8 sticky top-0 z-30 ${getThemeClass(cardClasses.bgColor, "bg-theme-surface")} ${getThemeClass(pageShellClasses.borderColor, "border-theme-border/70")}`}
    >
      <div className="flex items-center gap-4">
        {/* {!isSidebarOpen && (
          <button onClick={onOpenSidebar} className="md:hidden p-2">
            <Menu size={20} />
          </button>
        )} */}
        <h1
          className={`text-xl font-semibold hidden sm:block ${getThemeClass(pageShellClasses.textColor, "text-theme-text")}`}
        >
          {title.length > 30 ? "Lead Detail" : `${title}`}
        </h1>
      </div>

      <div className="flex items-center gap-4 md:gap-8">
        <div className="relative hidden lg:block">
          <Search
            className={`absolute left-3 top-1/2 -translate-y-1/2 ${getThemeClass(pageShellClasses.mutedTextColor, "text-theme-text-soft")}`}
            size={16}
          />
          {/* <input
            type="text"
            placeholder={searchPlaceholder || "Search..."}
            value={searchQuery || ""}
            onChange={(e) => onSearchChange?.(e.target.value)}
            className="pl-10 pr-4 py-2 bg-theme-surface-muted border border-theme-border rounded-lg text-sm w-64 focus:ring-2 focus:ring-theme-accent outline-none"
          /> */}
        </div>
        <div className="flex items-center gap-3">
          <button
            className={`p-2 rounded-full relative ${getThemeClass(pageShellClasses.mutedTextColor, "text-theme-text-soft")} hover:bg-theme-surface-muted`}
          >
            <Bell size={20} />
            <span
              className={`absolute top-2 right-2.5 w-2 h-2 bg-theme-danger-bright rounded-full border-2 ${getThemeClass(cardClasses.bgColor, "border-theme-surface")}`}
            ></span>
          </button>
          <UserAvatarDropdown
            userName={userName}
            menuItems={[
              { label: "Profile", onClick: () => console.log("Profile") },
              { label: "Settings", onClick: () => console.log("Settings") },
              {
                label: "Logout",
                danger: true,
                onClick: handleSignOut,
              },
            ]}
          />
        </div>
      </div>
    </header>
  );
}

const UserAvatarDropdown = ({ userName = "U", menuItems = [] }) => {
  const avatarClasses = getThemeComponentGroup("avatar");
  const menuClasses = getThemeComponentGroup("menu");
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="relative inline-block" ref={dropdownRef}>
      {/* Avatar */}
      <div
        onClick={() => setOpen(!open)}
        className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold cursor-pointer select-none ${getThemeClass(avatarClasses.badgeBgColor, "bg-theme-primary-alt")} ${getThemeClass(avatarClasses.badgeTextColor, "text-theme-text-inverse")}`}
      >
        {userName}
      </div>

      {/* Dropdown */}
      {open && (
        <div
          className={`absolute right-0 mt-2 w-44 shadow-lg rounded-md border z-50 ${getThemeClass(menuClasses.panelBgColor, "bg-theme-surface")} ${getThemeClass(menuClasses.panelBorderColor, "border-theme-border")}`}
        >
          <ul
            className={`py-1 text-sm ${getThemeClass(menuClasses.itemTextColor, "text-theme-text-muted")}`}
          >
            {menuItems.map((item, index) => (
              <li
                key={index}
                onClick={() => {
                  item.onClick?.();
                  setOpen(false);
                }}
                className={`px-4 py-2 cursor-pointer ${getThemeClass(menuClasses.itemHoverBgColor, "hover:bg-theme-surface-soft")} ${
                  item.danger ? "text-theme-danger hover:bg-theme-danger-bright-soft" : ""
                }`}
              >
                {item.label}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};
